import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Switch } from '../ui/switch';
import { Sparkles, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatHour(h, isDe) {
    if (h == null) return null;
    // Display UTC hour as local-ish 24h format; we keep UTC honest because that's
    // what the scheduler uses internally — labeled accordingly.
    return isDe ? `${String(h).padStart(2, '0')}:00 UTC` : `${String(h).padStart(2, '0')}:00 UTC`;
}

/**
 * Settings card to opt out of proactive "Smart Insight" pushes and to see
 * the user's discovered peak engagement hour. Pure transparency surface —
 * everything else (logging, sending) is backend-controlled.
 */
export function SmartPushCard({ language }) {
    const isDe = language === 'de';
    const [enabled, setEnabled] = useState(true);
    const [peakHour, setPeakHour] = useState(null);
    const [streak, setStreak] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API}/coach/insight-streak`);
                if (cancelled) return;
                setEnabled(res.data?.smart_push_enabled !== false);
                setPeakHour(res.data?.peak_hour ?? null);
                setStreak(res.data?.streak || 0);
            } catch (_e) { /* silent */ }
            finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, []);

    const handleToggle = async (next) => {
        setSaving(true);
        const prev = enabled;
        setEnabled(next);
        try {
            await axios.put(`${API}/coach/smart-push`, { enabled: next });
            toast.success(
                next
                    ? (isDe ? 'Smart-Push aktiviert' : 'Smart-Push enabled')
                    : (isDe ? 'Smart-Push deaktiviert' : 'Smart-Push disabled'),
                { duration: 2500 },
            );
        } catch (_e) {
            setEnabled(prev);
            toast.error(isDe ? 'Konnte nicht gespeichert werden' : 'Could not save');
        } finally { setSaving(false); }
    };

    if (loading) return null;

    const peakLabel = formatHour(peakHour, isDe);

    return (
        <Card data-testid="smart-push-card">
            <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-violet-500" />
                    {isDe ? 'Smart Insight Push' : 'Smart Insight Push'}
                </CardTitle>
                <CardDescription>
                    {isDe
                        ? 'Nanouk schickt dir 1× pro Tag eine kurze Push-Notification mit einem frischen Insight — aber nur zu deiner persönlichen Best-Zeit, nie öfter als 1×/24h.'
                        : 'Nanouk sends one short push per day with a fresh insight — only at your personal best-time, never more than once per 24h.'}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/40">
                    <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">
                            {isDe ? 'Aktiviert' : 'Enabled'}
                        </p>
                        <p className="text-xs text-muted-foreground leading-snug">
                            {enabled
                                ? (isDe ? 'Du erhältst Smart-Pushes' : 'You receive smart pushes')
                                : (isDe ? 'Nanouk hält sich proaktiv zurück' : 'Nanouk stays proactively quiet')}
                        </p>
                    </div>
                    <Switch
                        checked={enabled}
                        onCheckedChange={handleToggle}
                        disabled={saving}
                        data-testid="smart-push-toggle"
                    />
                </div>

                {peakLabel ? (
                    <div className="flex items-start gap-2 px-1 py-1" data-testid="smart-push-peak-info">
                        <Clock className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-muted-foreground leading-relaxed">
                            {isDe
                                ? <>Deine bisherige Best-Zeit ist <strong className="text-foreground">{peakLabel}</strong>. Nanouk wird Smart-Pushes dort senden, wenn neue Insights für dich verfügbar sind.</>
                                : <>Your peak engagement time so far is <strong className="text-foreground">{peakLabel}</strong>. Nanouk will time smart pushes around it when fresh insights are available.</>}
                        </p>
                    </div>
                ) : (
                    <div className="flex items-start gap-2 px-1 py-1" data-testid="smart-push-peak-empty">
                        <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-muted-foreground leading-relaxed">
                            {isDe
                                ? <>Nanouk braucht noch <strong className="text-foreground">{Math.max(5 - streak, 5)} (i)-Klicks</strong> auf personalisierte Prompts, um deine Best-Zeit zu erkennen.</>
                                : <>Nanouk needs a few more <strong className="text-foreground">(i)-taps</strong> on personalized prompts to learn your best time.</>}
                        </p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
